import { useCallback, useEffect, useRef } from "react";

import { useWriterComponentState } from "../useWriterComponentState";

export const BATCH_DEBOUNCE_MS = 800;

interface UseQuoteViewTrackerProps {
  shipmentId: number;
}

interface UseQuoteViewTrackerReturn {
  markQuoteViewed: (quoteId: number) => void;
}

/*
  Records that a shipper has viewed carrier quotes on a shipment. Quote ids
  passed to `markQuoteViewed` are accumulated and, after a short debounce so
  simultaneously-visible quotes coalesce, sent as one batched
  POST /shipments/{shipmentId}/quotes/view. Each quote is sent at most once per
  session; the backend additionally debounces repeat views.
*/
export function useQuoteViewTracker(
  props: UseQuoteViewTrackerProps,
): UseQuoteViewTrackerReturn {
  const { shipmentId } = props;

  const pendingQuoteIds = useRef<Set<number>>(new Set());
  const sentQuoteIds = useRef<Set<number>>(new Set());
  const debounceTimer = useRef<ReturnType<typeof setTimeout>>();

  const { write } = useWriterComponentState({
    errorMessage: "Failed to record quote views.",
    writeConfig: {
      method: "post",
      url: `/shipments/${shipmentId}/quotes/view`,
    },
  });

  // `write` is a fresh function each render (and closes over the current auth
  // token), so hold the latest in a ref. This lets `markQuoteViewed` stay
  // referentially stable — the per-row observers that depend on it are not torn
  // down and rebuilt on every parent re-render — while still flushing with an
  // up-to-date `write`.
  const writeRef = useRef(write);

  useEffect(() => {
    writeRef.current = write;
  }, [write]);

  const flushPendingViews = useCallback(() => {
    const quoteIds = Array.from(pendingQuoteIds.current);
    pendingQuoteIds.current.clear();
    if (quoteIds.length === 0) {
      return;
    }
    // Mark as sent only once the write succeeds. On failure the ids stay unsent
    // so a later dwell retries them, rather than being lost silently. A quote
    // that re-dwells while the write is in flight may be posted twice, which is
    // harmless because the backend debounces repeat views.
    writeRef.current(
      () => quoteIds.forEach((quoteId) => sentQuoteIds.current.add(quoteId)),
      { quote_ids: quoteIds },
    );
  }, []);

  const markQuoteViewed = useCallback(
    (quoteId: number) => {
      if (sentQuoteIds.current.has(quoteId)) {
        return;
      }
      pendingQuoteIds.current.add(quoteId);
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
      debounceTimer.current = setTimeout(flushPendingViews, BATCH_DEBOUNCE_MS);
    },
    [flushPendingViews],
  );

  // Flush on unmount so views marked within the debounce window are not dropped
  // when the shipper navigates away.
  useEffect(
    () => () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
      flushPendingViews();
    },
    [flushPendingViews],
  );

  return { markQuoteViewed };
}
